import { useDispatch } from 'react-redux';

import { Box, Flex, Icon, Image, InputBase, Text } from '@opendoor/bricks/core';
import { ScreenReaderOnly } from '@opendoor/bricks/utils';

import { ImageSelectQuestion } from 'src/lib/flow/FlowQuestion';
import { changeAnswers } from 'src/store/sellerInput';

import { trackAnswerChange } from './trackAnswerChange';

interface ImageSelectProps {
  name: string;
  onChange: (value: string) => void;
  isSelected: boolean;
  label: string;
  imageUrl: string;
  value: string;
  description: string;
}

const ImageSelect: React.FC<ImageSelectProps> = ({
  isSelected,
  label,
  imageUrl,
  value,
  description,
  onChange,
  name,
}) => {
  return (
    <Flex
      borderRadius="roundedSquare"
      overflow="hidden"
      cursor="pointer"
      boxShadow={isSelected ? 'z2' : 'z1'}
      width={['100%', null, '518px']}
      height="100px"
      tabIndex={0}
      onKeyPress={(event) => {
        if (event?.key === 'Enter') {
          onChange(value);
        }
      }}
    >
      <ScreenReaderOnly>
        <InputBase
          type="radio"
          role="radio"
          id={`${name}-${value}`}
          name={name}
          value={value}
          aria-checked={isSelected}
          onClick={() => onChange(value)}
        />
      </ScreenReaderOnly>
      <Box as="label" cursor="pointer" htmlFor={`${name}-${value}`} height="100%" width="100%">
        <Flex flexDir="row" width="100%" height="100%">
          <Image objectFit="cover" width={['130px', null, '151px']} alt={label} src={imageUrl} />
          <Flex
            flex={1}
            flexDir="column"
            px={[5, 5, 9]}
            py={[4, 4, 6]}
            justifyContent="center"
            alignItems="flex-start"
            height="100%"
            width="100%"
          >
            <Flex justifyContent="space-between" width="100%">
              <Text fontSize="s1" fontWeight="semibold" mb={1}>
                {label}
              </Text>
              {isSelected && (
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  backgroundColor="brand50"
                  height="22px"
                  width="22px"
                  borderRadius="rounded"
                >
                  <Icon name="checkmark" color="neutrals0" size={12} />
                </Flex>
              )}
            </Flex>
            <Text fontSize={['s00', null, 's0']}>{description}</Text>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
};

type ImageSelectInputProps = {
  question: ImageSelectQuestion;
  value?: string;
};

const ImageSelectInput: React.FC<ImageSelectInputProps> = ({ question, value }) => {
  const { options, key: name } = question;
  const dispatch = useDispatch();

  const onChange = (value?: string) => {
    dispatch(changeAnswers([{ key: name, answer: value ?? null }]));
    trackAnswerChange(name, value);
  };

  const selected = value ?? null;
  return (
    <Flex flexDir="column" px={2} justifyContent="center" gap="5">
      {options.map(({ value, label, description, imageUrl }) => {
        const isSelected = selected === value;
        return (
          <ImageSelect
            name={name}
            isSelected={isSelected}
            key={`option-${value}`}
            value={value as string}
            description={description}
            label={label}
            imageUrl={imageUrl}
            onChange={onChange}
          />
        );
      })}
    </Flex>
  );
};

export default ImageSelectInput;
